'use client';

import { useQuery } from '@tanstack/react-query';
import { getSubcatsClient } from '@util/firestore/categories';
import { CategoryDocument } from 'models/category';
import { useCreateListing } from '../_context';
import { ListingCard } from './ListingCard';

const mainCategoriesTitles = ['GT', 'Formula', 'Prototype'];

export const CreateListingStepThree = () => {
  const { form, setCurrentStep, setCategoryInfo, categoryInfo } =
    useCreateListing();

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories', '/categories_new/race-cars-for-sale'],
    queryFn: () =>
      getSubcatsClient(1, '/categories_new/race-cars-for-sale', true, true),
  });

  const errors = form.formState.errors.ride_types?.message;

  const goBack = () => setCurrentStep((prev) => prev - 1);
  const goFoward = () => setCurrentStep((prev) => prev + 1);

  const subCategoryOnClick = (c: CategoryDocument) => {
    // Set `ride_types` from the picked sport (mirrors mxlocker-web's
    // SelectListingSport). The vehicle landing search filters by
    // `ride_types:=[...]`; without this, vehicles never show on /<type>-cars-for-sale.
    const rideType = c.product_cats.ride_type;
    form.setValue('ride_types', rideType ? [rideType] : c.ride_types ?? []);
    setCategoryInfo((prev) =>
      categoryInfo.length === 2 ? [prev[0], c] : [...prev, c]
    );
    goFoward();
  };

  const mainCategories = categories
    ?.filter(
      (c) => mainCategoriesTitles.includes(c.app_desc) && c.index != undefined
    )
    .sort((c1, c2) => (c1.index ?? 0) - (c2.index ?? 0));

  const otherCategories = categories
    ?.filter(
      (c) => !mainCategoriesTitles.includes(c.app_desc) && c.index !== undefined
    )
    .sort((c1, c2) => (c1.index ?? 0) - (c2.index ?? 0));

  return (
    <div className="flex w-full flex-col justify-between px-8 pt-12 sm:mx-auto sm:max-w-[100rem] sm:py-32 md:pt-20">
      <h1 className="mb-12 text-heading-h5 font-bold text-black md:mb-20 md:text-heading-h4">
        Which sport is this item for?
      </h1>
      <div className="mb-12 lg:mb-20">
        <div className="mb-8">
          <span className="text-body-lg font-semibold">Road Racing</span>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-[220px] w-[165px] animate-pulse rounded bg-gray-200 md:h-[260px] md:w-[189px]"
              />
            ))
            : mainCategories?.map((category, i) => (
              <ListingCard
                key={i}
                category={category}
                onClick={subCategoryOnClick}
                selectedCategory={categoryInfo[1]}
              />
            ))}
        </div>
      </div>

      <div className="mb-12">
        <div className="mb-8">
          <span className="text-body-lg font-semibold">Others</span>
        </div>
        <div className=" grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-[220px] w-[165px] animate-pulse rounded bg-gray-200 md:h-[260px] md:w-[189px]"
              />
            ))
            : otherCategories?.map((category, i) => (
              <ListingCard
                key={i}
                category={category}
                onClick={subCategoryOnClick}
                selectedCategory={categoryInfo[1]}
              />
            ))}
        </div>
      </div>

      <div className="flex w-full flex-col items-center gap-[1rem] py-8 md:py-12">
        {errors && <p className="text-[1.5rem] text-brand-red">{errors}</p>}
        <div className="flex w-full">
          <button
            className="h-[44px] w-full rounded-full border border-black text-center text-[1.875rem] font-semibold md:w-[184px]"
            type="button"
            onClick={goBack}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};
